import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';

const EventImageCarousel = ({ coverImage, photoAlbum = [], title, height = 250 }) => {
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  
  // Combiner l'image de couverture avec l'album photo
  const allImages = [];
  
  // Ajouter l'image de couverture en premier si elle existe
  if (coverImage) {
    allImages.push(coverImage);
  }
  
  // Ajouter les photos de l'album
  if (photoAlbum && photoAlbum.length > 0) {
    photoAlbum.forEach(photo => {
      // Éviter les doublons si cover_image est aussi dans l'album
      if (photo !== coverImage) {
        allImages.push(photo);
      }
    });
  }
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  
  // Si aucune image, ne rien afficher
  if (allImages.length === 0) {
    return null;
  }
  
  // Construire l'URL complète de l'image
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${API_BASE}${imageUrl}`;
  };
  
  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  }, [allImages.length]);
  
  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  }, [allImages.length]);
  
  const goToSlide = (index) => {
    setCurrentIndex(index);
  };
  
  // Ouvrir le lightbox
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden'; // Bloquer le scroll
  };
  
  // Fermer le lightbox
  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
  };
  
  // Navigation dans le lightbox
  const lightboxPrevious = () => {
    setLightboxIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  };
  
  const lightboxNext = () => {
    setLightboxIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  };
  
  // Auto-rotation du carousel (seulement si plusieurs images et pas survolé)
  useEffect(() => {
    if (allImages.length > 1 && !isHovered && !lightboxOpen) {
      const interval = setInterval(() => {
        goToNext();
      }, 4000); // Change toutes les 4 secondes
      
      return () => clearInterval(interval);
    }
  }, [allImages.length, isHovered, lightboxOpen, goToNext]);
  
  // Fermer lightbox avec Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowLeft') {
        lightboxPrevious();
      } else if (e.key === 'ArrowRight') {
        lightboxNext();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);
  
  // Si une seule image (seulement la couverture)
  if (allImages.length === 1) {
    return (
      <div className="mb-6">
        <div 
          className="relative overflow-hidden rounded-lg cursor-pointer group"
          style={{ height: `${height}px` }}
          onClick={() => openLightbox(0)}
        >
          <img 
            src={getImageUrl(allImages[0])} 
            alt={title}
            className="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
          />
          {/* Icône zoom au survol */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center pointer-events-none">
            <ZoomIn className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
        </div>
        
        {/* Lightbox */}
        {lightboxOpen && (
          <Lightbox 
            images={allImages}
            currentIndex={lightboxIndex}
            onClose={closeLightbox}
            onPrevious={lightboxPrevious}
            onNext={lightboxNext}
            getImageUrl={getImageUrl}
            title={title}
          />
        )}
      </div>
    );
  }
  
  // Carrousel avec plusieurs images
  return (
    <div 
      className="mb-6 relative group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image principale */}
      <div 
        className="relative overflow-hidden rounded-lg cursor-pointer"
        style={{ height: `${height}px` }}
        onClick={() => openLightbox(currentIndex)}
      >
        <img 
          src={getImageUrl(allImages[currentIndex])} 
          alt={`${title} - Image ${currentIndex + 1}`}
          className="w-full h-full object-cover object-center transition-all duration-500"
        />
        
        {/* Overlay au survol */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center pointer-events-none">
          <ZoomIn className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>
        
        {/* Compteur d'images */}
        <div 
          className="absolute bottom-3 right-3 px-3 py-1 rounded-full text-sm font-medium"
          style={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            color: 'white',
            fontFamily: '"Lora", serif'
          }}
        >
          {currentIndex + 1} / {allImages.length}
        </div>
        
        {/* Barre de progression auto-rotation */}
        {!isHovered && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-300 bg-opacity-50">
            <div 
              className="h-full transition-all ease-linear"
              style={{ 
                backgroundColor: 'rgb(196, 150, 96)',
                animation: 'progressBar 4s linear infinite'
              }}
            />
          </div>
        )}
      </div>
      
      {/* Bouton Précédent */}
      <button
        onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
        className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
        style={{ 
          backgroundColor: 'rgba(196, 150, 96, 0.9)',
          color: 'white'
        }}
        aria-label="Image précédente"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      
      {/* Bouton Suivant */}
      <button
        onClick={(e) => { e.stopPropagation(); goToNext(); }}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
        style={{ 
          backgroundColor: 'rgba(196, 150, 96, 0.9)',
          color: 'white'
        }}
        aria-label="Image suivante"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
      
      {/* Indicateurs (points) */}
      <div className="flex justify-center gap-2 mt-3">
        {allImages.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2 h-2 rounded-full transition-all duration-200 ${
              index === currentIndex 
                ? 'w-6' 
                : 'opacity-50 hover:opacity-75'
            }`}
            style={{ 
              backgroundColor: index === currentIndex 
                ? 'rgb(196, 150, 96)' 
                : 'rgb(200, 200, 200)'
            }}
            aria-label={`Aller à l'image ${index + 1}`}
          />
        ))}
      </div>
      
      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox 
          images={allImages}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrevious={lightboxPrevious}
          onNext={lightboxNext}
          getImageUrl={getImageUrl}
          title={title}
        />
      )}
      
      {/* CSS pour l'animation de la barre de progression */}
      <style>{`
        @keyframes progressBar {
          0% { width: 0%; }
          100% { width: 100%; }
        }
      `}</style>
    </div>
  );
};

// Composant Lightbox optimisé avec préchargement
const Lightbox = ({ images, currentIndex, onClose, onPrevious, onNext, getImageUrl, title }) => {
  const [loadedImages, setLoadedImages] = React.useState(new Set());
  const [isLoading, setIsLoading] = React.useState(true);

  // Fonction pour obtenir l'URL du thumbnail
  const getThumbnailUrl = (url) => {
    const fullUrl = getImageUrl(url);
    if (fullUrl.includes('/api/files/') && !fullUrl.includes('?')) {
      return `${fullUrl}?thumb=true`;
    }
    return fullUrl;
  };

  // Précharger les images adjacentes
  React.useEffect(() => {
    const preload = (index) => {
      if (index >= 0 && index < images.length) {
        const img = new Image();
        img.onload = () => setLoadedImages(prev => new Set([...prev, index]));
        img.src = getImageUrl(images[index]);
      }
    };
    
    preload(currentIndex);
    preload(currentIndex + 1);
    preload(currentIndex - 1);
  }, [currentIndex, images, getImageUrl]);

  React.useEffect(() => {
    setIsLoading(!loadedImages.has(currentIndex));
  }, [currentIndex, loadedImages]);

  const handleImageLoad = () => {
    setLoadedImages(prev => new Set([...prev, currentIndex]));
    setIsLoading(false);
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)' }}
      onClick={onClose}
    >
      {/* Bouton fermer */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full text-white hover:bg-white hover:bg-opacity-20 transition-colors z-50"
        aria-label="Fermer"
      >
        <X className="w-8 h-8" />
      </button>
      
      {/* Image principale avec loader */}
      <div 
        className="relative max-w-[90vw] max-h-[85vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
        style={{ minWidth: '200px', minHeight: '200px' }}
      >
        {/* Loader */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        
        <img 
          src={getImageUrl(images[currentIndex])} 
          alt={`${title} - Image ${currentIndex + 1}`}
          className={`max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl transition-opacity duration-300 ${
            isLoading ? 'opacity-0' : 'opacity-100'
          }`}
          onLoad={handleImageLoad}
        />
      </div>
      
      {/* Navigation */}
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onPrevious(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full text-white hover:bg-white hover:bg-opacity-20 transition-colors"
            aria-label="Image précédente"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full text-white hover:bg-white hover:bg-opacity-20 transition-colors"
            aria-label="Image suivante"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
        </>
      )}
      
      {/* Compteur */}
      <div 
        className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full text-white text-sm"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
      >
        {currentIndex + 1} / {images.length}
      </div>
      
      {/* Miniatures en bas - utilise les thumbnails */}
      {images.length > 1 && images.length <= 10 && (
        <div 
          className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2 p-2 rounded-lg"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => {
                // Mettre à jour l'index du lightbox
                const event = new CustomEvent('lightboxNavigate', { detail: index });
                window.dispatchEvent(event);
              }}
              className={`w-12 h-8 rounded overflow-hidden border-2 transition-all duration-200 ${
                index === currentIndex 
                  ? 'border-white' 
                  : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <img 
                src={getThumbnailUrl(image)} 
                alt={`Miniature ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventImageCarousel;
